// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SummaryCard, {
  NothingToSummarise,
} from "../SynoptophoreGraph/shared/SummaryCard";
import { FigureRow, Figures } from "../SynoptophoreGraph/shared/FigureRow";
import TargetFigure from "./TargetFigure";

// Types
import {
  AniseikoniaMeridianKeyType,
  AniseikoniaMeridianResultType,
  AniseikoniaMeridianSummaryType,
  AniseikoniaTargetGeometryType,
} from "src/services/exams/types";

// Utils
import { pct, signed } from "../SynoptophoreGraph/shared/formatters";

type MeridianCardType = {
  meridian: AniseikoniaMeridianKeyType;
  title: string;
  summary: AniseikoniaMeridianSummaryType | null | undefined;
  result: AniseikoniaMeridianResultType | undefined;
  geometry: AniseikoniaTargetGeometryType | undefined;
};

const MeridianCard = ({
  meridian,
  title,
  summary,
  result,
  geometry,
}: MeridianCardType) => {
  const { t } = useTranslation();

  // A derived meridian was taken as the mean of the other two rather than measured, so it has no
  // settings, no probes and no range. Saying so is kinder than printing dashes.
  const isDerived = Boolean(result?.derived);

  // The last setting the patient actually made, which is the closest thing to "what they ended
  // up looking at". A derived meridian has none.
  const lastSetting = result?.settings
    ?.filter((setting) => setting.view)
    ?.pop();

  return (
    <SummaryCard
      title={
        <>
          <Box component="span">{title}</Box>
          <Typography
            component="span"
            fontSize="10px"
            sx={{
              border: "1px solid currentColor",
              borderRadius: "3px",
              padding: "1px 5px",
              color: isDerived ? "#8B94A1" : "#22BE39",
            }}
          >
            {isDerived ? t("Derived") : t("Measured")}
          </Typography>
        </>
      }
      cautions={null}
    >
      {!summary ? (
        <NothingToSummarise />
      ) : (
        <Box display="flex" flexWrap="wrap" gap="16px" sx={{ pb: "8px" }}>
          <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
            <Figures>
              <FigureRow
                label={t("Image Size Difference")}
                value={
                  <Typography fontSize="20px" fontWeight={600}>
                    {signed(summary.percent, 1)}%
                  </Typography>
                }
                sub={
                  summary.significant
                    ? t("Above Detection Floor")
                    : t("Below Detection Floor")
                }
              />
              <FigureRow
                label={t("Larger Image")}
                value={summary.largerEye ? t(summary.largerEye) : t("N/A")}
              />
              {/* The range the four settings fell in. It is NOT a 95% interval -- four settings
                  do not support one -- so it must never be labelled as one. */}
              <FigureRow
                label={t("Range")}
                value={
                  summary.ci
                    ? `${pct(summary.ci[0], 1)} – ${pct(summary.ci[1], 1)}`
                    : t("N/A")
                }
              />
              <FigureRow
                label={t("Settings Made")}
                value={result?.settingCount ?? 0}
              />
              {/* Three states. A meridian measured once has no second setting to agree
                  with, which is not the same finding as two that came out far apart -- and
                  since one setting per meridian is the default, showing them alike would put
                  "Settings Disagree" on the ordinary section. */}
              <FigureRow
                label={
                  summary.settingsAgree === null
                    ? t("Not Replicated")
                    : summary.settingsAgree
                      ? t("Settings Agree")
                      : t("Settings Disagree")
                }
                value={
                  summary.settingsAgree === null
                    ? t("N/A")
                    : pct(result?.approachSpreadPercent, 2)
                }
                sub={t("Approach Spread")}
              />
              <FigureRow
                label={t("Reference Eye Delta")}
                value={pct(result?.referenceEyeGroupDeltaPercent, 2)}
              />
            </Figures>
            {isDerived && (
              <Typography fontSize="11px" color="#5B6D7A" sx={{ mt: "8px" }}>
                {t(
                  "Not measured — derived from the horizontal and vertical meridians."
                )}
              </Typography>
            )}
          </Box>

          {lastSetting?.view && (
            <Box sx={{ flex: "0 0 auto" }}>
              <Typography
                fontSize="12px"
                color="#5B6D7A"
                sx={{ textTransform: "uppercase", mb: "4px" }}
              >
                {t("What the Patient Saw")}
              </Typography>
              <TargetFigure
                geometry={geometry}
                view={lastSetting.view}
                meridian={meridian}
                caption={t("As Set")}
              />
            </Box>
          )}
        </Box>
      )}
    </SummaryCard>
  );
};

export default MeridianCard;
